<script setup lang="ts">
import { app } from '@/composition/app'
import { GroupState } from '@/composition/groups'
import { PostState } from '@/composition/posts'
import { useCommunitiesToastNotification } from '@/helper/communitiesToastNotification.helper'
import {
  CheckIcon,
  ChevronLeftIcon,
  XCloseIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UIButton,
  UIDrawer,
  UIDrawerContent,
  UIModal,
  UIModalHeader,
  UISelect,
  UITextLgMedium,
  UITextMdMedium,
} from '@gohighlevel/ghl-ui'
import { computed, defineAsyncComponent, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import CommunityButton from '../common/buttons/CommunityButton.vue'

const UIConfirmation = defineAsyncComponent(
  () => import('@/components/common/modal/UIConfirmation.vue')
)

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  postId: {
    type: String,
    required: false,
  },
  currentChannel: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['update:show', 'postUpdated'])

const { t } = useI18n()
const route = useRoute()
const notification = useCommunitiesToastNotification()
const selectedChannel = ref<string>('')
const channelOptions = ref<
  { label: string; value: string; visibility?: string }[]
>([])
const isMoving = ref(false)
const showPrivateChannelConfirmation = ref(false)
const pendingChannelId = ref<string | null>(null)

const HEADER_HEIGHT = 60
const CHANNEL_ITEM_HEIGHT = 56.5
const MAX_VIEWPORT_HEIGHT_PERCENTAGE = 0.6 // 60% of viewport height

const drawerHeight = computed(() => {
  const channelsCount = channelOptions.value.length
  // Calculate total content height (header + all channels)
  const contentHeight = HEADER_HEIGHT + channelsCount * CHANNEL_ITEM_HEIGHT

  // Get maximum allowed height (60% of viewport)
  const maxHeight = window.innerHeight * MAX_VIEWPORT_HEIGHT_PERCENTAGE

  // Return the smaller of the two values
  return Math.min(contentHeight, maxHeight)
})

// Update the modal open watch to only handle channel fetching
watch(
  () => props.show,
  async newValue => {
    if (newValue) {
      await fetchChannels()
      selectedChannel.value = props.currentChannel.name
    }
  }
)

const fetchChannels = async () => {
  try {
    // await ChannelState.fetchChannels()
    // Transform channel list into options format and only show public channels
    // also remove the current channel from the list
    channelOptions.value =
      GroupState.state.nonRestrictedChannelList
        ?.filter(channel => channel._id !== props.currentChannel._id)
        .map(channel => ({
          label: channel.name,
          value: channel._id,
          visibility: channel.visibility,
        })) || []
  } catch (error) {
    console.error('Error fetching channels:', error)
    notification.create({
      type: 'error',
      title: 'Unable to fetch channels. Please try again.',
      duration: 3000,
    })
  }
}

const handleMove = async () => {
  if (!selectedChannel.value) {
    notification.create({
      type: 'error',
      title: 'Please select a channel',
      duration: 3000,
    })
    return
  }

  // Check if the selected channel is private
  const selectedChannelOption = channelOptions.value.find(
    option => option.value === selectedChannel.value
  )

  if (selectedChannelOption?.visibility === 'PRIVATE') {
    // Store the channel ID to move to after confirmation
    pendingChannelId.value = selectedChannel.value
    showPrivateChannelConfirmation.value = true
    return
  }

  // If not private, proceed with move directly
  await performMove(selectedChannel.value)
}

const performMove = async (channelId: string) => {
  isMoving.value = true
  try {
    const selectedChannelName = channelOptions.value.find(
      option => option.value === channelId
    )?.label
    const updatedPost = await PostState.movePostToChannel(
      props.postId,
      channelId,
      route
    )
    emit('postUpdated', updatedPost)
    closeModal()
    notification.create({
      type: 'success',
      title: t('post.postMovedToChannel', {
        channelName: selectedChannelName,
      }),
      duration: 3000,
    })
  } catch (error) {
    console.error('Error moving post:', error)
    notification.create({
      type: 'error',
      title: 'Unable to move post. Please try again.',
      duration: 3000,
    })
  } finally {
    isMoving.value = false
    pendingChannelId.value = null
  }
}

const handleConfirmMoveToPrivate = async () => {
  showPrivateChannelConfirmation.value = false
  if (pendingChannelId.value) {
    await performMove(pendingChannelId.value)
  }
}

const handleClosePrivateChannelConfirmation = (value: boolean) => {
  showPrivateChannelConfirmation.value = value
  if (!value) {
    pendingChannelId.value = null
  }
}

const closeModal = () => {
  selectedChannel.value = ''
  emit('update:show', false)
}
</script>

<template>
  <UIModal
    v-if="!app.state.isMobile"
    id="move-to-channel-modal"
    :show="show"
    class-name="fixed top-0 bottom-0 md:relative"
    :width="400"
    :z-index="2000"
    @update:show="closeModal"
  >
    <template #header>
      <UIModalHeader id="modal-header" type="custom" @close="closeModal">
        <template #customHeader>
          <div class="flex flex-row items-center justify-between pb-5">
            <UITextLgMedium class="text-communities-font-primary">{{
              t('post.movePostToChannel')
            }}</UITextLgMedium>
            <XCloseIcon
              class="h-5 w-6 text-communities-font-primary"
              @click="closeModal"
            />
          </div>
        </template>
      </UIModalHeader>
    </template>

    <div class="flex flex-col gap-4">
      <UISelect
        id="select-channel"
        v-model:value="selectedChannel"
        :options="channelOptions"
        :filterable="true"
      />

      <div class="grid w-full grid-cols-2 gap-4 overflow-hidden">
        <CommunityButton
          id="cancel-move-modal__btn"
          button-type="tertiary"
          class="w-full"
          :disabled="isMoving"
          @click="closeModal"
        >
          {{ t('common.cancel') }}
        </CommunityButton>
        <CommunityButton
          id="move-modal__btn"
          button-type="primary"
          class="w-full"
          :loading="isMoving"
          :disabled="
            !selectedChannel || selectedChannel === props.currentChannel.name
          "
          @click="handleMove"
        >
          {{ t('common.move') }}
        </CommunityButton>
      </div>
    </div>
  </UIModal>

  <!-- Updated Mobile Bottom Sheet -->
  <UIDrawer
    v-else
    id="move-channel-drawer"
    class="rounded-t-xl"
    :show="show"
    placement="bottom"
    :height="drawerHeight"
    :z-index="2006"
    @update:show="closeModal"
  >
    <UIDrawerContent
      id="move-channel-drawer-menu"
      v-swipe="{
        direction: 'down',
        callback: () => closeModal(),
      }"
      class="flex flex-col"
      :closable="false"
    >
      <!-- Sticky Header -->
      <div
        class="sticky top-0 z-10 flex items-center justify-between rounded-t-2xl border-0 border-b border-solid border-communities-border bg-communities-background px-4 py-4"
      >
        <ChevronLeftIcon
          class="mr-4 h-5 w-6 cursor-pointer"
          @click="closeModal"
        />

        <UITextLgMedium class="text-communities-font-primary">{{
          t('post.movePostToChannel')
        }}</UITextLgMedium>

        <UIButton
          id="move-post-to-channel-btn"
          :text="true"
          :disabled="
            !selectedChannel || selectedChannel === props.currentChannel.name
          "
          :loading="isMoving"
          @click="handleMove"
        >
          <UITextMdMedium class="font-semibold">{{
            t('common.move')
          }}</UITextMdMedium>
        </UIButton>
      </div>

      <!-- Scrollable Channel List -->
      <div class="flex-1 overflow-y-auto">
        <div
          v-for="channel in channelOptions"
          :key="channel.value"
          class="px-4 py-4"
          :class="{
            'border-0 border-b border-solid border-communities-border': true,
          }"
          tabindex="0"
          role="button"
          :aria-label="`Move to ${channel.label} channel`"
          @keyup.enter="selectedChannel = channel.value"
          @click="selectedChannel = channel.value"
        >
          <div class="flex items-center justify-between">
            <span class="text-base text-communities-font-primary">{{
              channel.label
            }}</span>
            <CheckIcon
              v-if="selectedChannel === channel.value"
              class="h-5 w-5 text-communities-tertiary"
            />
          </div>
        </div>
      </div>
    </UIDrawerContent>
  </UIDrawer>

  <!-- Confirmation Modal for Private Channel -->
  <UIConfirmation
    :show="showPrivateChannelConfirmation"
    :height="300"
    type="warning"
    :title="t('post.moveToPrivateChannelConfirmation.title')"
    button-type="primary"
    :message="t('post.moveToPrivateChannelConfirmation.message')"
    :positive-text="t('common.continue')"
    @update:show="handleClosePrivateChannelConfirmation"
    @update:confirmed="handleConfirmMoveToPrivate"
  />
</template>

<style scoped>
/* Hide scrollbar for Chrome, Safari and Opera */
.overflow-y-auto::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.overflow-y-auto {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

/* Optional: Add a subtle shadow to the sticky header */
.sticky {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
</style>
